import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { I18nService } from '../core/i18n.service';
import { SeoService } from '../core/seo.service';
import { SiteConfigService } from '../core/site-config.service';
import { AnalyticsService } from '../core/analytics.service';
import { MarkdownHtmlPipe } from '../shared/markdown-html.pipe';
import { ResponsiveImageComponent } from '../shared/responsive-image.component';
import { LocalDatePipe } from '../shared/local-date.pipe';
import { BlogItem, BlogService } from '../widgets/blog/blog.service';

@Component({
  selector: 'app-blog-detail-page',
  imports: [LocalDatePipe, MarkdownHtmlPipe, ResponsiveImageComponent, RouterLink],
  template: `
    <section
      class="blog-detail"
      [class.blog-detail--global]="variant() === 'globalEditorial'"
      [class.blog-detail--mystic]="variant() === 'mysticFortune'"
    >
      @if (item(); as blog) {
        <article class="blog-detail-card">
          <a class="back-link" [routerLink]="i18n.routePath('blog')">{{ copy().back }}</a>
          <p class="eyebrow">{{ (blog.publishedAt || blog.createdAt || blog.updatedAt) ? ((blog.publishedAt || blog.createdAt || blog.updatedAt) | localDate: 'date') : 'Blog' }} · {{ blog.readingMinute || 1 }} {{ copy().minute }}</p>
          <h1>{{ blog.title }}</h1>
          @if (blog.imageUrl) {
            <app-responsive-image
              class="cover"
              [url]="blog.imageUrl"
              [mediaSizes]="blog.imageSizes"
              [alt]="blog.title"
              displaySizes="(max-width: 900px) 92vw, 1040px"
            />
          }
          <div class="markdown" [innerHTML]="blog.markdown | markdownHtml" (click)="trackArticleLink($event, blog)"></div>
        </article>
      } @else if (loading()) {
        <article class="blog-detail-card">
          <p class="eyebrow">Blog</p>
          <h1>{{ copy().loading }}</h1>
        </article>
      } @else {
        <article class="blog-detail-card not-found-card">
          <p class="eyebrow">404</p>
          <h1>{{ copy().notFound }}</h1>
          <p>{{ copy().notFoundLead }}</p>
          <a class="back-link" [routerLink]="i18n.routePath('blog')">{{ copy().back }}</a>
        </article>
      }
    </section>
  `,
  styleUrl: '../widgets/blog/blog-widgets.scss'
})
export class BlogDetailPage {
  private readonly route = inject(ActivatedRoute);
  protected readonly i18n = inject(I18nService);
  private readonly blogService = inject(BlogService);
  private readonly site = inject(SiteConfigService);
  private readonly seo = inject(SeoService);
  private readonly analytics = inject(AnalyticsService);
  protected readonly item = signal<BlogItem | null>(null);
  protected readonly loading = signal(true);
  protected readonly variant = computed(() => this.site.config().design.widgets['blogDetail'] ?? 'globalEditorial');
  protected readonly copy = computed(() => ({
    tr: { back: 'Bloglara dön', minute: 'dk okuma', loading: 'Yazı yükleniyor', fallback: 'Blog yazısını oku.', home: 'Ana sayfa', notFound: 'Aradığın yazı bulunamadı', notFoundLead: 'Bu yazı kaldırılmış, taşınmış veya hiç yayınlanmamış olabilir.' },
    en: { back: 'Back to blog', minute: 'min read', loading: 'Loading article', fallback: 'Read the blog article.', home: 'Home', notFound: 'Article not found', notFoundLead: 'This article may have been removed, moved, or never published.' },
    de: { back: 'Zurück zum Blog', minute: 'Min. Lesezeit', loading: 'Beitrag wird geladen', fallback: 'Lies den Blogbeitrag.', home: 'Startseite', notFound: 'Beitrag nicht gefunden', notFoundLead: 'Dieser Beitrag wurde möglicherweise entfernt oder verschoben.' },
    es: { back: 'Volver al blog', minute: 'min de lectura', loading: 'Cargando artículo', fallback: 'Lee el artículo del blog.', home: 'Inicio', notFound: 'Artículo no encontrado', notFoundLead: 'Este artículo puede haber sido eliminado o movido.' },
    fr: { back: 'Retour au blog', minute: 'min de lecture', loading: 'Chargement de l’article', fallback: 'Lire l’article du blog.', home: 'Accueil', notFound: 'Article introuvable', notFoundLead: 'Cet article a peut-être été supprimé ou déplacé.' }
  }[this.i18n.lang()]));

  constructor() {
    void this.load();
  }

  protected trackArticleLink(event: MouseEvent, blog: BlogItem): void {
    const anchor = event.target instanceof Element ? event.target.closest('a') : null;
    if (!(anchor instanceof HTMLAnchorElement)) return;
    const href = anchor.getAttribute('href') || '';
    if (!href) return;
    let url: URL;
    try {
      url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'https://local.invalid');
    } catch {
      return;
    }
    if (!/^\/(?:fal-baktir|ucretsiz-fal)(?:\/|$)/i.test(url.pathname)) return;
    this.analytics.track('blog.service_click', {
      entityType: 'blog',
      entityID: blog.id,
      entityTitle: blog.title,
      metadata: { targetPath: url.pathname, linkText: anchor.textContent?.trim().slice(0, 180) || '' }
    });
  }

  private async load(): Promise<void> {
    const slug = this.route.snapshot.paramMap.get('slug') ?? '';
    if (!slug) {
      this.loading.set(false);
      return;
    }

    try {
      const item = await this.blogService.detail(slug);
      this.item.set(item);
      if (item) {
        this.analytics.identifyPage('blog', item.id, item.title);
        this.seo.setPage({
          title: item.metaTitle || item.title,
          description: item.metaDescription || item.preview || this.copy().fallback,
          image: item.ogImageUrl || item.imageUrl,
          path: this.i18n.routePath('blogDetail', { slug: item.slug }),
          type: 'article',
          publishedAt: item.publishedAt || item.createdAt,
          modifiedAt: item.updatedAt || item.createdAt,
          breadcrumbs: [
            { name: this.copy().home, path: this.i18n.routePath('home') },
            { name: 'Blog', path: this.i18n.routePath('blog') },
            { name: item.title, path: this.i18n.routePath('blogDetail', { slug: item.slug }) }
          ]
        });
      } else {
        this.seo.setNotFound(this.copy().notFound, this.copy().notFoundLead);
      }
    } catch {
      this.item.set(null);
      this.seo.setNotFound(this.copy().notFound, this.copy().notFoundLead);
    } finally {
      this.loading.set(false);
    }
  }
}
